import apiClient, { getHttpStatus } from './client';
import { apiRequestError } from './errors';
import type { AxiosError } from 'axios';
import type {
  ApiResponse,
  BillingStatus,
  BillingInvoice,
  MigrateResponse,
  CheckoutSession,
  PlanPricesResponse,
  AddSubscriptionRequest,
  AddSubscriptionResponse,
  UpgradeOptionsResponse,
  UpgradePlanRequest,
  UpgradePlanResponse,
  UpgradePreviewResponse,
  IntervalSwitchPreviewResponse,
  IntervalSwitchResponse,
} from '../types';

interface ApiErrorResponse {
  success: false;
  error: string;
  message: string;
  balance_amount?: number;
  balance_currency?: string;
}

/**
 * Get billing status for current user's organization
 */
export async function getBillingStatus(): Promise<BillingStatus> {
  const response = await apiClient.get<BillingStatus>('/api/v1/billing');
  if (!response.data.success) {
    throw new Error('Failed to get billing status');
  }
  return response.data;
}

/**
 * Create a Stripe customer portal session
 * Returns URL to redirect user to Stripe's billing portal
 */
export async function createPortalSession(): Promise<{ url: string }> {
  const response = await apiClient.post<ApiResponse<{ url: string }>>(
    '/api/v1/billing/portal-session'
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || 'Failed to create portal session'
    );
  }
  return response.data.data;
}

/**
 * Create a Stripe checkout session for migration from Foxy/Billsby
 * Returns URL to redirect user to Stripe Checkout
 */
export async function createMigrationCheckout(): Promise<MigrateResponse> {
  try {
    const response = await apiClient.post<ApiResponse<MigrateResponse>>(
      '/api/v1/billing/migrate'
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.message || 'Failed to create migration checkout'
      );
    }
    return response.data.data;
  } catch (error) {
    // Extract error message from API response if available
    const axiosError = error as AxiosError<ApiErrorResponse>;
    if (axiosError.response?.data?.message) {
      throw new Error(axiosError.response.data.message);
    }
    throw error;
  }
}

/**
 * Verify a Stripe checkout session after redirect
 * Used on the success page to show confirmation
 */
export async function verifyCheckoutSession(
  sessionId: string
): Promise<CheckoutSession> {
  const response = await apiClient.get<ApiResponse<CheckoutSession>>(
    `/api/v1/billing/checkout/${encodeURIComponent(sessionId)}`
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || 'Failed to verify checkout session'
    );
  }
  return response.data.data;
}

/**
 * Get available plan prices for adding a subscription
 * Returns prices in the customer's currency
 */
export async function getPlanPrices(): Promise<PlanPricesResponse> {
  const response = await apiClient.get<ApiResponse<PlanPricesResponse>>(
    '/api/v1/billing/plans'
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get plan prices');
  }
  return response.data.data;
}

/**
 * Create a Stripe checkout session for adding a new subscription
 * Returns URL to redirect user to Stripe Checkout
 */
export async function createAddSubscriptionCheckout(
  request: AddSubscriptionRequest
): Promise<AddSubscriptionResponse> {
  const response = await apiClient.post<ApiResponse<AddSubscriptionResponse>>(
    '/api/v1/billing/add-subscription',
    request
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || 'Failed to create checkout session'
    );
  }
  return response.data.data;
}

/**
 * Create a Stripe checkout session for reactivating a suspended/archived subscription
 * Returns URL to redirect user to Stripe Checkout
 */
export async function reactivateSubscription(
  subscriptionId: string
): Promise<{ checkout_url: string }> {
  const response = await apiClient.post<
    ApiResponse<{ checkout_url: string }>
  >('/api/v1/billing/reactivate', { subscription_id: subscriptionId });
  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || 'Failed to create reactivation checkout'
    );
  }
  return response.data.data;
}

/**
 * Purchase dedicated IP add-on for a subscription
 */
export async function purchaseDedicatedIp(
  subscriptionId: string,
  /** The quote's due_today the customer saw (credit period only); the API refuses a mismatch. */
  confirmDueToday?: number
): Promise<{ billing_added: boolean; provisioning_started: boolean; message?: string }> {
  try {
    const response = await apiClient.post<{
      success: boolean;
      billing_added?: boolean;
      provisioning_started?: boolean;
      error?: string;
      message?: string;
    }>('/api/v1/billing/dedicated-ip', {
      subscription_id: subscriptionId,
      ...(confirmDueToday !== undefined ? { confirm_due_today: confirmDueToday } : {}),
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to add dedicated IP');
    }
    return {
      billing_added: response.data.billing_added ?? false,
      provisioning_started: response.data.provisioning_started ?? false,
      message: response.data.message,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    if (axiosError.response?.data?.message) {
      throw new Error(axiosError.response.data.message);
    }
    throw error;
  }
}

export interface DedicatedIpPrice {
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  /**
   * Credit period only (a migrated customer's Stripe trial): the one-off charge
   * on Confirm that covers the DIP until credit_period_end (ISO timestamp).
   */
  due_today?: number;
  credit_period_end?: string;
}

/**
 * Get the dedicated IP add-on price for a subscription, in the customer's
 * billing currency and interval (month/year)
 */
export async function getDedicatedIpPrice(subscriptionId: string): Promise<DedicatedIpPrice> {
  const response = await apiClient.get<ApiResponse<DedicatedIpPrice>>(
    '/api/v1/billing/dedicated-ip/price',
    { params: { subscription_id: subscriptionId } }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get dedicated IP price');
  }
  return response.data.data;
}

/**
 * Get per-subscription plan upgrade options for the current organization
 */
export async function getUpgradeOptions(): Promise<UpgradeOptionsResponse> {
  const response = await apiClient.get<ApiResponse<UpgradeOptionsResponse>>(
    '/api/v1/billing/upgrade-options'
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to get upgrade options');
  }
  return response.data.data;
}

/**
 * Preview the prorated charge for a stripe_instant plan upgrade
 * Returns amount due today and the (unchanged) renewal date
 */
export async function previewUpgradePlan(
  request: UpgradePlanRequest
): Promise<UpgradePreviewResponse> {
  const response = await apiClient.post<ApiResponse<UpgradePreviewResponse>>(
    '/api/v1/billing/upgrade-plan/preview',
    request
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to preview upgrade');
  }
  return response.data.data;
}

/**
 * Execute a plan upgrade. The response `flow` discriminates the outcome:
 * - stripe_instant: charged in place, container resize queued
 * - legacy_checkout: redirect the customer to checkout_url
 * - request: upgrade request recorded for the team
 */
export async function upgradePlan(
  request: UpgradePlanRequest
): Promise<UpgradePlanResponse> {
  try {
    const response = await apiClient.post<ApiResponse<UpgradePlanResponse>>(
      '/api/v1/billing/upgrade-plan',
      request
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to upgrade plan');
    }
    return response.data.data;
  } catch (error) {
    // Surface API error messages (e.g. card declined on the in-place swap),
    // preserving the error slug so the UI can branch on 'possible_desync'
    throw apiRequestError(error, 'Failed to upgrade plan');
  }
}

/**
 * Preview the charge for switching a subscription from monthly to annual billing
 */
export async function previewIntervalSwitch(
  subscriptionId: string
): Promise<IntervalSwitchPreviewResponse> {
  try {
    const response = await apiClient.post<ApiResponse<IntervalSwitchPreviewResponse>>(
      '/api/v1/billing/interval-switch/preview',
      { subscription_id: subscriptionId }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.message || 'Failed to preview interval switch'
      );
    }
    return response.data.data;
  } catch (error) {
    // 400 = an eligibility/data reason with a customer-worded message from
    // the API (e.g. not_active while a migrated-legacy sub is in its credit
    // period: "contact support"). Surface it with its slug so the modal can
    // show the real reason. Anything else (502 stripe_error carrying raw
    // upstream text, 5xx, network) stays a bare error → generic modal copy.
    if (getHttpStatus(error) === 400) {
      throw apiRequestError(error, 'Failed to preview interval switch');
    }
    throw error;
  }
}

/**
 * Switch a subscription from monthly to annual billing (charges immediately)
 */
export async function switchToAnnual(
  subscriptionId: string
): Promise<IntervalSwitchResponse> {
  try {
    const response = await apiClient.post<ApiResponse<IntervalSwitchResponse>>(
      '/api/v1/billing/interval-switch',
      { subscription_id: subscriptionId }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(
        response.data.message || 'Failed to switch to annual billing'
      );
    }
    return response.data.data;
  } catch (error) {
    // Surface API error messages (e.g. payment_failed on card decline,
    // possible_desync if the charge succeeded but the interval flip failed),
    // preserving the error slug so the UI can branch on it
    throw apiRequestError(error, 'Failed to switch to annual billing');
  }
}

/**
 * Cancel a pending plan upgrade request
 */
export async function cancelUpgradeRequest(requestId: string): Promise<void> {
  try {
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `/api/v1/billing/upgrade-request/${encodeURIComponent(requestId)}`
    );
    if (!response.data.success) {
      throw new Error(
        response.data.message || 'Failed to cancel upgrade request'
      );
    }
  } catch (error) {
    // Surface API error messages (e.g. 400 not_pending while an admin is
    // mid-completion) instead of a bare axios error
    const axiosError = error as AxiosError<ApiErrorResponse>;
    if (axiosError.response?.data?.message) {
      throw new Error(axiosError.response.data.message);
    }
    throw error;
  }
}

/**
 * Invoices of the org's invoice-billed subscriptions (empty for card customers).
 */
export async function getBillingInvoices(): Promise<BillingInvoice[]> {
  const response = await apiClient.get<{ success: boolean; invoices: BillingInvoice[] }>('/api/v1/billing/invoices');
  if (!response.data.success) {
    throw new Error('Failed to get invoices');
  }
  return response.data.invoices;
}
